/**
 * Bedazzled Text — accumulating rhinestone fill animation.
 *
 * The DOM contains a real <span> with the text (kept transparent) so the host
 * sizes naturally with CSS and the text is still selectable / SEO-friendly /
 * screen-reader accessible. All visible rendering happens in the canvas:
 * the text is drawn first, then gems are progressively layered on top inside
 * the letter shapes. Because the visible text and the hit mask come from the
 * same canvas draw with the same font metrics, the gems can never drift out
 * of alignment with the letters.
 *
 * Each gem is a fake-3D 2D rhinestone: 8 crown facets around a central table,
 * with per-facet brightness driven by the current light direction. Light is
 * a blend of pointer position and a slow ambient drift so the whole word
 * shimmers as you move the mouse — and even when idle.
 */

class BedazzledText extends HTMLElement {
  #canvas = null;
  #ctx = null;
  #textEl = null;
  #hitData = null;
  #gems = [];
  #raf = 0;
  #startTime = 0;
  #visible = false;
  #io = null;
  #ro = null;
  #dpr = 1;
  #cssW = 0;
  #cssH = 0;
  #fontPx = 280;
  #image = null;
  #imageReady = false;
  #lightX = 0.3;
  #lightY = -0.3;
  #targetLightX = 0.3;
  #targetLightY = -0.3;
  #pointerActive = false;
  #pointerTimer = 0;
  #fontReady = false;

  connectedCallback() {
    this.text = (this.dataset.text || 'Belozzled').trim();
    this.duration = Math.max(2, parseFloat(this.dataset.duration || '30')) * 1000;
    this.maxGems = parseInt(this.dataset.maxGems || '550', 10);
    this.minRadius = parseFloat(this.dataset.minRadius || '6');
    this.maxRadius = parseFloat(this.dataset.maxRadius || '14');
    this.strictness = Math.max(0, Math.min(1, parseFloat(this.dataset.strictness || '0.85')));
    this.loop = this.dataset.loop === 'true';
    this.loopDelay = parseFloat(this.dataset.loopDelay || '4') * 1000;
    this.spacing = Math.max(1.0, Math.min(2.0, parseFloat(this.dataset.spacing || '1.0')));
    this.fitWidth = this.dataset.fitWidth !== 'false';
    this.maxFontSize = Math.max(0, parseFloat(this.dataset.maxFontSize || '0'));
    this.allowWrap = this.dataset.allowWrap === 'true';
    this.imageUrl = this.dataset.image || '';

    if (this.imageUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { this.#imageReady = true; };
      img.src = this.imageUrl;
      this.#image = img;
    }

    this.#textEl = this.querySelector('.bedazzled-text__text');
    if (!this.#textEl) {
      this.#textEl = document.createElement('span');
      this.#textEl.className = 'bedazzled-text__text';
      this.#textEl.textContent = this.text;
      this.appendChild(this.#textEl);
    } else {
      this.text = this.#textEl.textContent.trim() || this.text;
    }

    this.#canvas = document.createElement('canvas');
    this.#canvas.className = 'bedazzled-text__canvas';
    this.#canvas.setAttribute('aria-hidden', 'true');
    this.appendChild(this.#canvas);

    this.#ctx = this.#canvas.getContext('2d');

    this.#ro = new ResizeObserver(() => this.#layout());
    this.#ro.observe(this);

    this.#io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        this.#visible = e.isIntersecting;
        if (this.#visible && !this.#raf) this.#raf = requestAnimationFrame(this.#tick);
      }
    });
    this.#io.observe(this);

    window.addEventListener('pointermove', this.#onPointerMove, { passive: true });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        this.#fontReady = true;
        this.#buildText();
        this.#gems.length = 0;
        this.#startTime = performance.now();
        this.#render(this.#startTime);
      });
    } else {
      this.#fontReady = true;
    }
    this.#layout();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#raf);
    this.#raf = 0;
    this.#ro?.disconnect();
    this.#io?.disconnect();
    window.removeEventListener('pointermove', this.#onPointerMove);
  }

  #onPointerMove = (e) => {
    const rect = this.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const reach = Math.max(rect.width, rect.height) * 0.7;
    this.#targetLightX = Math.max(-1, Math.min(1, (e.clientX - cx) / reach));
    this.#targetLightY = Math.max(-1, Math.min(1, (e.clientY - cy) / reach));
    this.#pointerActive = true;
    this.#pointerTimer = performance.now();
  };

  #layout() {
    let rect = this.getBoundingClientRect();
    if (!rect.width) return;

    if (this.fitWidth) this.#fitTextToWidth(rect.width);

    rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const sizeChanged = rect.width !== this.#cssW || rect.height !== this.#cssH || dpr !== this.#dpr;
    this.#cssW = rect.width;
    this.#cssH = rect.height;
    this.#dpr = dpr;
    this.#canvas.style.width = rect.width + 'px';
    this.#canvas.style.height = rect.height + 'px';
    this.#canvas.width = Math.round(rect.width * dpr);
    this.#canvas.height = Math.round(rect.height * dpr);
    this.#ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    if (sizeChanged) {
      this.#buildText();
      this.#gems.length = 0;
      this.#startTime = performance.now();
      this.#render(this.#startTime);
    }
  }

  #fitTextToWidth(availableWidth) {
    const REF = 100;
    this.#textEl.style.fontSize = REF + 'px';
    const cs = getComputedStyle(this.#textEl);

    let drawText = this.text;
    const tt = cs.textTransform;
    if (tt === 'uppercase') drawText = drawText.toUpperCase();
    else if (tt === 'lowercase') drawText = drawText.toLowerCase();
    else if (tt === 'capitalize') drawText = drawText.replace(/\b\w/g, (c) => c.toUpperCase());

    const probe = document.createElement('canvas').getContext('2d');
    probe.font = `${cs.fontStyle} ${cs.fontWeight} ${REF}px ${cs.fontFamily}`;
    if ('letterSpacing' in probe) probe.letterSpacing = cs.letterSpacing;
    if ('fontKerning' in probe) probe.fontKerning = 'normal';

    let measured;
    if (this.allowWrap) {
      const words = drawText.split(/\s+/).filter(Boolean);
      measured = 0;
      for (const w of words) {
        const mw = probe.measureText(w).width;
        if (mw > measured) measured = mw;
      }
      if (!measured) measured = probe.measureText(drawText).width;
    } else {
      measured = probe.measureText(drawText).width;
    }
    if (!measured) return;

    let target = availableWidth * (REF / measured);
    if (this.maxFontSize > 0) target = Math.min(target, this.maxFontSize);
    target = Math.max(12, target);
    this.#textEl.style.fontSize = target + 'px';
  }

  #buildText() {
    const cs = getComputedStyle(this.#textEl);
    const fontSize = parseFloat(cs.fontSize);
    this.#fontPx = fontSize;

    let lineHeight;
    if (cs.lineHeight === 'normal') {
      lineHeight = fontSize * 1.2;
    } else if (cs.lineHeight.endsWith('px')) {
      lineHeight = parseFloat(cs.lineHeight);
    } else {
      lineHeight = fontSize * parseFloat(cs.lineHeight);
    }

    const dpr = this.#dpr;
    const fontStr = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;

    const tt = cs.textTransform;
    const applyCase = (s) => {
      if (tt === 'uppercase') return s.toUpperCase();
      if (tt === 'lowercase') return s.toLowerCase();
      if (tt === 'capitalize') return s.replace(/\b\w/g, (c) => c.toUpperCase());
      return s;
    };

    const setupCtx = (ctx) => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.font = fontStr;
      ctx.textBaseline = 'alphabetic';
      ctx.textAlign = 'left';
      if ('letterSpacing' in ctx) ctx.letterSpacing = cs.letterSpacing;
      if ('fontKerning' in ctx) ctx.fontKerning = 'normal';
    };

    const probe = document.createElement('canvas').getContext('2d');
    setupCtx(probe);
    const m0 = probe.measureText(applyCase(this.text));
    const fa = m0.fontBoundingBoxAscent || m0.actualBoundingBoxAscent || fontSize * 0.8;
    const fd = m0.fontBoundingBoxDescent || m0.actualBoundingBoxDescent || fontSize * 0.2;
    const baselineFromBoxTop = (lineHeight + fa - fd) / 2;

    const textRect = this.#textEl.getBoundingClientRect();
    const hostRect = this.getBoundingClientRect();
    const align = cs.textAlign || 'left';

    const lines = this.#computeLines(textRect, hostRect, lineHeight, applyCase);

    const W = Math.max(1, Math.round(this.#cssW * dpr));
    const H = Math.max(1, Math.round(this.#cssH * dpr));

    const hit = document.createElement('canvas');
    hit.width = W;
    hit.height = H;
    const hctx = hit.getContext('2d', { willReadFrequently: true });
    setupCtx(hctx);
    hctx.fillStyle = '#000';

    for (const line of lines) {
      let lineX;
      if (align === 'center') {
        const lw = probe.measureText(line.text).width;
        lineX = (this.#cssW - lw) / 2;
      } else if (align === 'right') {
        const lw = probe.measureText(line.text).width;
        lineX = this.#cssW - lw;
      } else {
        lineX = line.x;
      }
      const lineY = line.topRel + baselineFromBoxTop;
      hctx.fillText(line.text, lineX, lineY);
    }
    this.#hitData = hctx.getImageData(0, 0, W, H);

    if (this.dataset.loaded !== 'true') {
      this.dataset.loaded = 'true';
    }
  }

  #computeLines(textRect, hostRect, lineHeight, applyCase) {
    const textNode = this.#textEl.firstChild;
    if (!this.allowWrap || !textNode || textNode.nodeType !== Node.TEXT_NODE) {
      return [{
        text: applyCase(this.text),
        x: textRect.left - hostRect.left,
        topRel: textRect.top - hostRect.top,
      }];
    }

    const raw = textNode.nodeValue;
    const tokens = [];
    const re = /\S+/g;
    let m;
    while ((m = re.exec(raw)) !== null) {
      tokens.push({ raw: m[0], start: m.index, end: m.index + m[0].length });
    }
    if (!tokens.length) {
      return [{
        text: applyCase(this.text),
        x: textRect.left - hostRect.left,
        topRel: textRect.top - hostRect.top,
      }];
    }

    const range = document.createRange();
    const grouped = new Map();
    for (const t of tokens) {
      range.setStart(textNode, t.start);
      range.setEnd(textNode, t.end);
      const wr = range.getBoundingClientRect();
      const idx = Math.max(0, Math.round((wr.top - textRect.top) / lineHeight));
      let line = grouped.get(idx);
      if (!line) {
        line = { idx, words: [], left: Infinity };
        grouped.set(idx, line);
      }
      line.words.push(t.raw);
      if (wr.left < line.left) line.left = wr.left;
    }

    return Array.from(grouped.values())
      .sort((a, b) => a.idx - b.idx)
      .map((l) => ({
        text: applyCase(l.words.join(' ')),
        x: l.left - hostRect.left,
        topRel: (textRect.top - hostRect.top) + l.idx * lineHeight,
      }));
  }

  #pixelInLetter(cx, cy) {
    if (!this.#hitData) return false;
    const dpr = this.#dpr;
    const w = this.#hitData.width;
    const h = this.#hitData.height;
    const px = Math.round(cx * dpr);
    const py = Math.round(cy * dpr);
    if (px < 0 || px >= w || py < 0 || py >= h) return false;
    return this.#hitData.data[(py * w + px) * 4 + 3] > 24;
  }

  #fitsInLetter(cx, cy, r) {
    if (!this.#pixelInLetter(cx, cy)) return false;
    const ringSamples = 8;
    let inside = 0;
    for (let i = 0; i < ringSamples; i++) {
      const a = (i / ringSamples) * Math.PI * 2;
      const sx = cx + Math.cos(a) * r * 0.78;
      const sy = cy + Math.sin(a) * r * 0.78;
      if (this.#pixelInLetter(sx, sy)) inside++;
    }
    const required = Math.round(ringSamples * this.strictness);
    return inside >= required;
  }

  #tryAddGem() {
    if (this.#gems.length >= this.maxGems) return false;
    const tries = 32;
    const spacing = this.spacing;
    const scale = Math.pow(this.#fontPx / 280, 0.75);
    const minR = this.minRadius * scale;
    const maxR = this.maxRadius * scale;

    for (let t = 0; t < tries; t++) {
      const x = Math.random() * this.#cssW;
      const y = Math.random() * this.#cssH;
      if (!this.#pixelInLetter(x, y)) continue;

      let allowed = maxR;
      for (let j = this.#gems.length - 1; j >= 0; j--) {
        const g = this.#gems[j];
        const dx = g.x - x;
        const dy = g.y - y;
        const d = Math.sqrt(dx * dx + dy * dy);
        const cap = d / spacing - g.r;
        if (cap < allowed) allowed = cap;
        if (allowed < minR) break;
      }
      if (allowed < minR) continue;

      const r = Math.min(allowed, maxR);
      if (!this.#fitsInLetter(x, y, r)) continue;

      this.#gems.push({
        x, y, r,
        rot: Math.random() * Math.PI * 2,
        tint: 0.94 + Math.random() * 0.06,
        hueShift: Math.round(-180 + Math.random() * 360),
        pulsePhase: Math.random() * Math.PI * 2,
        pulseRate: 0.0006 + Math.random() * 0.0009,
        bornAt: performance.now(),
      });
      return true;
    }
    return false;
  }

  #tick = (now) => {
    if (!this.#visible) {
      this.#raf = requestAnimationFrame(this.#tick);
      return;
    }
    if (!this.#hitData && this.#fontReady) this.#buildText();

    const elapsed = now - this.#startTime;
    const progress = Math.min(1, elapsed / this.duration);
    const eased = 1 - Math.pow(1 - progress, 1.6);
    const targetCount = Math.round(eased * this.maxGems);

    let attempts = 0;
    while (this.#gems.length < targetCount && attempts < 14) {
      if (!this.#tryAddGem()) break;
      attempts++;
    }

    if (this.loop && progress >= 1 && elapsed > this.duration + this.loopDelay) {
      this.#gems.length = 0;
      this.#startTime = now;
    }

    if (this.#pointerActive && now - this.#pointerTimer > 2500) this.#pointerActive = false;
    const ambientX = Math.sin(now * 0.0006) * 0.45;
    const ambientY = Math.cos(now * 0.0008) * 0.35;
    const blend = this.#pointerActive ? 0.7 : 0.0;
    const tx = this.#targetLightX * blend + ambientX * (1 - blend * 0.4);
    const ty = this.#targetLightY * blend + ambientY * (1 - blend * 0.4);
    this.#lightX += (tx - this.#lightX) * 0.07;
    this.#lightY += (ty - this.#lightY) * 0.07;

    this.#render(now);
    this.#raf = requestAnimationFrame(this.#tick);
  };

  #render(now) {
    const ctx = this.#ctx;
    ctx.clearRect(0, 0, this.#cssW, this.#cssH);
    const gems = this.#gems;
    for (let i = 0; i < gems.length; i++) this.#drawGem(ctx, gems[i], now);
  }

  #drawGem(ctx, g, now) {
    const { x, y, r, rot, tint, hueShift, pulsePhase, pulseRate, bornAt } = g;
    const lx = this.#lightX;
    const ly = this.#lightY;

    const age = Math.min(1, (now - bornAt) / 320);
    const eased = 1 - Math.pow(1 - age, 3);
    const rr = r * (0.2 + 0.8 * eased);

    const pulse = 0.55 + 0.45 * Math.sin(now * pulseRate + pulsePhase);

    if (this.#imageReady) {
      const size = rr * 2;

      const haloR = rr * (1.4 + pulse * 0.3);
      const haloAlpha = 0.12 + pulse * 0.18;
      const haloGrad = ctx.createRadialGradient(x, y, rr * 0.85, x, y, haloR);
      haloGrad.addColorStop(0, `rgba(255,255,255,${haloAlpha})`);
      haloGrad.addColorStop(0.5, `rgba(255,255,255,${haloAlpha * 0.35})`);
      haloGrad.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = haloGrad;
      ctx.beginPath();
      ctx.arc(x, y, haloR, 0, Math.PI * 2);
      ctx.fill();

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rot);
      if (hueShift) ctx.filter = `hue-rotate(${hueShift}deg)`;
      ctx.drawImage(this.#image, -size / 2, -size / 2, size, size);
      ctx.restore();

      ctx.save();
      ctx.beginPath();
      ctx.arc(x, y, rr * 0.97, 0, Math.PI * 2);
      ctx.clip();
      ctx.globalCompositeOperation = 'screen';

      const rimGrad = ctx.createRadialGradient(x, y, rr * 0.55, x, y, rr * 0.98);
      rimGrad.addColorStop(0, 'rgba(255,255,255,0)');
      rimGrad.addColorStop(0.8, `rgba(255,255,255,${0.18 * tint})`);
      rimGrad.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = rimGrad;
      ctx.beginPath();
      ctx.arc(x, y, rr * 0.97, 0, Math.PI * 2);
      ctx.fill();

      const hx = x - lx * rr * 0.35;
      const hy = y - ly * rr * 0.35;
      const hr = rr * 0.5;
      const intensity = 0.55 * tint;
      const grad = ctx.createRadialGradient(hx, hy, 0, hx, hy, hr);
      grad.addColorStop(0, `rgba(255,255,255,${intensity})`);
      grad.addColorStop(0.6, `rgba(255,255,255,${intensity * 0.3})`);
      grad.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(hx, hy, hr, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
      return;
    }

    const N = 8;
    const tau = Math.PI * 2;
    const tableR = rr * 0.46;

    const outer = new Array(N);
    const inner = new Array(N);
    for (let i = 0; i < N; i++) {
      const ao = rot + (i / N) * tau;
      const cao = Math.cos(ao);
      const sao = Math.sin(ao);
      outer[i] = { ax: cao, ay: sao, x: x + cao * rr, y: y + sao * rr };
      const ai = rot + ((i + 0.5) / N) * tau;
      inner[i] = { x: x + Math.cos(ai) * tableR, y: y + Math.sin(ai) * tableR };
    }

    ctx.beginPath();
    for (let i = 0; i < N; i++) {
      const p = outer[i];
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
    ctx.fillStyle = `rgba(28,38,55,${0.95 * age})`;
    ctx.fill();

    for (let i = 0; i < N; i++) {
      const o1 = outer[i];
      const o2 = outer[(i + 1) % N];
      const im = inner[i];

      const nx = (o1.ax + o2.ax) * 0.5;
      const ny = (o1.ay + o2.ay) * 0.5;
      const nlen = Math.hypot(nx, ny) || 1;
      const nnx = nx / nlen;
      const nny = ny / nlen;

      const dot = -nnx * lx - nny * ly;
      const bright = Math.max(0, dot * 0.5 + 0.5);
      const power = Math.pow(bright, 1.6);

      const rC = Math.round(135 + power * 120);
      const gC = Math.round(150 + power * 105);
      const bC = Math.round(180 + power * 75);

      ctx.beginPath();
      ctx.moveTo(o1.x, o1.y);
      ctx.lineTo(o2.x, o2.y);
      ctx.lineTo(im.x, im.y);
      ctx.closePath();
      ctx.fillStyle = `rgba(${rC},${gC},${bC},${0.95 * tint * age})`;
      ctx.fill();
    }

    ctx.beginPath();
    for (let i = 0; i < N; i++) {
      const p = inner[i];
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
    const hx = x - lx * tableR * 0.5;
    const hy = y - ly * tableR * 0.5;
    const tableGrad = ctx.createRadialGradient(hx, hy, 0, x, y, tableR * 1.1);
    tableGrad.addColorStop(0, `rgba(255,255,255,${0.98 * tint * age})`);
    tableGrad.addColorStop(0.5, `rgba(225,235,250,${0.92 * tint * age})`);
    tableGrad.addColorStop(1, `rgba(180,200,225,${0.85 * tint * age})`);
    ctx.fillStyle = tableGrad;
    ctx.fill();

    if (rr > 5) {
      ctx.beginPath();
      for (let i = 0; i < N; i++) {
        const o = outer[i];
        const im1 = inner[(i + N - 1) % N];
        const im2 = inner[i];
        ctx.moveTo(o.x, o.y);
        ctx.lineTo(im1.x, im1.y);
        ctx.moveTo(o.x, o.y);
        ctx.lineTo(im2.x, im2.y);
      }
      ctx.lineWidth = Math.max(0.5, rr * 0.045);
      ctx.strokeStyle = `rgba(255,255,255,${0.32 * age})`;
      ctx.stroke();
    }

    const sparkle = (0.7 + 0.3 * Math.sin(now * sparkleRate + sparklePhase)) * age;
    const hr = tableR * 0.65;
    const spec = ctx.createRadialGradient(hx, hy, 0, hx, hy, hr);
    spec.addColorStop(0, `rgba(255,255,255,${sparkle})`);
    spec.addColorStop(0.5, `rgba(255,255,255,${0.4 * sparkle})`);
    spec.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = spec;
    ctx.beginPath();
    ctx.arc(hx, hy, hr, 0, Math.PI * 2);
    ctx.fill();

    if (rr > 8 && sparkle > 0.6) {
      const sl = rr * 0.85 * sparkle;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rot);
      ctx.globalCompositeOperation = 'screen';
      const star = ctx.createLinearGradient(-sl, 0, sl, 0);
      star.addColorStop(0, 'rgba(255,255,255,0)');
      star.addColorStop(0.5, `rgba(255,255,255,${0.8 * sparkle})`);
      star.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = star;
      ctx.fillRect(-sl, -0.7, sl * 2, 1.4);
      ctx.fillRect(-0.7, -sl, 1.4, sl * 2);
      ctx.restore();
    }
  }
}

if (!customElements.get('bedazzled-text')) {
  customElements.define('bedazzled-text', BedazzledText);
}
